import {
  AttachapiApi,
  Configuration,
  GeojsonApi,
  MapapiApi,
  SrsapiApi,
  TableapiApi,
} from '@datum-group/isogd-openapi/lib/';
import axios from 'axios';
import { APICartograms } from './resources/cartograms';
import { APIExport } from './resources/export';
import { APIGeoJSONSearch } from './resources/geojson';
import { APIGeolocate } from './resources/geolocate';
import { auth } from './resources/GisRoAuth';
import { APISendMessages } from './resources/message';
import {
  NewsApi,
  NewsCategories,
  NewsGeoJSONLayer,
  NewsPhotos,
  NewsBulkPublication,
} from './resources/news';
import {
  EventsApi,
  EventsGeoJSONLayer,
  EventsPhotos,
} from './resources/events';
import { APISearch } from './resources/search';
import { GetSettings } from './resources/settings';
import { APITableSearch } from './resources/tableSearch';
import { APIUser } from './resources/users';
import { APIStatistics } from '@project/API/resources/statistics';
import { APIPrivacyPolicy } from './resources/privacyPolicy';
import {
  UserLayerAPI,
  UserLayerGeoJSONLayer,
  UserLayerPhotos,
  UserLayerRecordsApi,
} from './resources/userLayer';
import { isApiError } from '@project/API/utils';

const BACKEND_URL = process.env.BACKEND_URL;

export const axiosInstance = axios.create({ baseURL: BACKEND_URL });
export const apiConfig = new Configuration({
  basePath: BACKEND_URL,
});

axiosInstance.interceptors.request.use(async (request) => {
  const access = await auth.getAccessToken();

  if (!access) return request;

  const headers = request.headers ?? {};

  request.headers = {
    ...headers,
    Authorization: `Bearer ${access}`,
    // Для Safari
    Token: `Bearer ${access}`,
  };

  return request;
});

export class RequestError extends Error {
  readonly fields: Record<string, string | string[]>;
  readonly detail: string | undefined;

  constructor(
    errorData: Record<string, string | string[]>,
    fallbackMsg: string = 'Произошла ошибка при попытке отправить запрос на сервер',
  ) {
    if (typeof errorData?.['detail'] === 'string') {
      super(errorData['detail']);
      this.detail = errorData['detail'];

      delete errorData['detail'];
      delete errorData['type'];
    } else {
      super(fallbackMsg);
    }

    this.fields = errorData;
  }
}

axiosInstance.interceptors.response.use(
  async (response) => {
    return response;
  },
  async (e) => {
    if (
      e.config &&
      e.response &&
      e.response.status === 403 &&
      !e.config._isRetry
    ) {
      e.config._isRetry = true;

      await auth.refresh();

      return axiosInstance.request(e.config);
    }

    if (
      e.config &&
      e.response &&
      e.response.status === 401 &&
      !e.config._isRetry
    ) {
      e.config._isRetry = true;

      await auth.logout();

      return axiosInstance.request(e.config);
    }

    const data = e?.response?.data;

    if (isApiError(data)) {
      const error = new RequestError(data);

      return Promise.reject(error);
    }

    return Promise.reject(e);
  },
);

// TODO: Вынести ISOGD API в отдельный пакет и переиспользовать между репозиториями.
export const services = {
  export: new APIExport(),
  messages: new APISendMessages(),
  geolocate: new APIGeolocate(),
  geojsonSearch: new APIGeoJSONSearch(),
  search: new APISearch(),
  cartograms: new APICartograms(axiosInstance),
  tableSearch: new APITableSearch(),
  auth,
  users: new APIUser(axiosInstance),
  userLayerRecords: new UserLayerRecordsApi(axiosInstance),
  userLayerGeoJSONLayer: new UserLayerGeoJSONLayer(axiosInstance),
  userLayer: new UserLayerAPI(axiosInstance),
  userLayerPhotos: new UserLayerPhotos(axiosInstance),
  settings: new GetSettings(),
  news: new NewsApi(axiosInstance),
  privacyPolicy: new APIPrivacyPolicy(),
  newsCategories: new NewsCategories(axiosInstance),
  newsGeoJSONLayer: new NewsGeoJSONLayer(axiosInstance),
  newsPhotos: new NewsPhotos(axiosInstance),
  newsBulkPublication: new NewsBulkPublication(axiosInstance),
  events: new EventsApi(axiosInstance),
  eventsGeoJSONLayer: new EventsGeoJSONLayer(axiosInstance),
  eventsPhotos: new EventsPhotos(axiosInstance),
  table: new TableapiApi(apiConfig, undefined, undefined),
  srs: new SrsapiApi(apiConfig, undefined, undefined),
  map: new MapapiApi(apiConfig, undefined, undefined),
  records: new GeojsonApi(apiConfig, undefined, undefined),
  attachment: new AttachapiApi(apiConfig, undefined, undefined),
  statistics: new APIStatistics(),
};

export type Services = typeof services;
